import type { PasteSummary, SessionUser } from '../../shared/types'
import { ApiError, deletePaste, listPastes } from '../api'
import { buildFrame } from '../frame'
import { languageLabel } from '../languages'
import { forgetPalettePastas } from '../palette'
import { internalLink } from '../router'
import { saucePicker } from '../saucepicker'
import {
  formatBytes,
  formatCountdown,
  formatRelative,
  h,
  plural,
  snippetNodes,
  toast,
} from '../ui'

/** Long enough that typing a word doesn't fire a query per keystroke. */
const SEARCH_DEBOUNCE_MS = 180

export function listView(root: HTMLElement, user: SessionUser): () => void {
  const frame = buildFrame({ user, active: 'mine' })
  let disposed = false
  /** Everything, fetched once, so clearing the box costs no round trip. */
  let all: PasteSummary[] = []
  /** What's on screen — `all`, or the server's answer to the current query. */
  let rows: PasteSummary[] = []
  let query = ''
  let searching = false
  let timer: number | null = null
  /** Guards against a slow response for an old query landing after a newer one. */
  let generation = 0

  const filterInput = h('input', {
    class: 'title-input',
    type: 'search',
    placeholder: 'Search titles and contents',
    'aria-label': 'Search pastas',
    oninput: (event: Event) => {
      query = (event.target as HTMLInputElement).value.trim()
      if (timer !== null) clearTimeout(timer)
      if (!query) {
        searching = false
        rows = all
        renderRows()
        return
      }
      searching = true
      updateStatus()
      timer = window.setTimeout(() => void runSearch(query), SEARCH_DEBOUNCE_MS)
    },
  })

  frame.controls.append(
    filterInput,
    saucePicker(() => {}),
    h('a', { class: 'control primary', ...internalLink('/') }, ['New pasta']),
  )

  const body = h('div', { class: 'listing' })
  frame.stage.append(body)
  root.append(frame.element)
  document.title = 'my pastas · pastabin'
  frame.statusLeft.textContent = 'loading…'

  void load()

  async function load(): Promise<void> {
    try {
      const result = await listPastes()
      if (disposed) return
      all = result.pastes
      if (!query) rows = all
      renderRows()
    } catch (error) {
      if (disposed) return
      body.replaceChildren(
        h('div', { class: 'notice' }, [
          h('h1', {}, ['Could not load your pastas']),
          h('p', {}, [error instanceof ApiError ? error.message : 'Something went wrong.']),
        ]),
      )
      frame.statusLeft.textContent = 'error'
    }
  }

  async function runSearch(term: string): Promise<void> {
    const mine = ++generation
    try {
      const result = await listPastes(term)
      if (disposed || mine !== generation) return
      rows = result.pastes
      searching = false
      renderRows()
    } catch (error) {
      if (disposed || mine !== generation) return
      searching = false
      toast(error instanceof ApiError ? error.message : 'Search failed')
      updateStatus()
    }
  }

  function updateStatus(): void {
    if (searching) {
      frame.statusLeft.textContent = 'searching…'
      return
    }
    frame.statusLeft.textContent = query
      ? `${rows.length} of ${plural(all.length, 'pasta')}`
      : plural(all.length, 'pasta')
  }

  function renderRows(): void {
    updateStatus()

    if (!all.length) {
      body.replaceChildren(
        h('div', { class: 'notice' }, [
          h('h1', {}, ['Nothing saved yet']),
          h('p', {}, ['Scripts, runbooks, notes — anything you paste here stays under your account.']),
          h('div', { class: 'actions' }, [
            h('a', { class: 'control primary', ...internalLink('/') }, ['Write the first one']),
          ]),
        ]),
      )
      return
    }

    if (!rows.length) {
      body.replaceChildren(
        h('div', { class: 'notice' }, [h('p', {}, [`Nothing matches “${query}”.`])]),
      )
      return
    }

    const table = h('table', { class: 'table' }, [
      h('thead', {}, [
        h('tr', {}, [
          h('th', {}, ['Title']),
          h('th', { class: 'hide-sm' }, ['Language']),
          h('th', {}, ['Visibility']),
          h('th', { class: 'hide-sm' }, ['Size']),
          h('th', {}, ['Created']),
          h('th', { class: 'hide-sm' }, ['Expires']),
          h('th', {}, ['']),
        ]),
      ]),
      h(
        'tbody',
        {},
        rows.map((paste) => row(paste)),
      ),
    ])

    body.replaceChildren(table)
  }

  function row(paste: PasteSummary): HTMLElement {
    const tr = h('tr', {}, [
      h('td', {}, [
        h('a', { class: 'row-title', ...internalLink(`/p/${paste.id}`) }, [paste.title ?? 'untitled']),
        h('span', { class: 'row-id' }, [paste.id]),
        paste.snippet ? h('div', { class: 'row-snippet' }, snippetNodes(paste.snippet)) : null,
      ]),
      h('td', { class: 'hide-sm muted' }, [languageLabel(paste.language)]),
      h('td', {}, [
        h('span', { class: `badge ${paste.visibility}` }, [paste.visibility]),
        paste.burnAfterRead ? h('span', { class: 'badge burn' }, ['one serving']) : null,
      ]),
      h('td', { class: 'hide-sm muted' }, [formatBytes(paste.sizeBytes)]),
      h('td', { class: 'muted', title: new Date(paste.createdAt).toLocaleString() }, [
        formatRelative(paste.createdAt),
      ]),
      h('td', { class: 'hide-sm muted' }, [
        paste.expiresAt === null ? 'never' : `in ${formatCountdown(paste.expiresAt)}`,
      ]),
      h('td', { class: 'row-actions' }, [
        h('a', { class: 'control', ...internalLink(`/e/${paste.id}`) }, ['Edit']),
        h(
          'button',
          {
            class: 'control',
            type: 'button',
            onclick: () => void remove(paste, tr),
          },
          ['Delete'],
        ),
      ]),
    ])
    return tr
  }

  async function remove(paste: PasteSummary, tr: HTMLElement): Promise<void> {
    const name = paste.title ?? paste.id
    if (!confirm(`Delete “${name}”? This can't be undone.`)) return
    try {
      await deletePaste(paste.id)
      forgetPalettePastas()
      if (disposed) return
      all = all.filter((candidate) => candidate.id !== paste.id)
      rows = rows.filter((candidate) => candidate.id !== paste.id)
      tr.remove()
      renderRows()
      toast('Deleted')
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'Could not delete that pasta')
    }
  }

  return () => {
    disposed = true
    if (timer !== null) clearTimeout(timer)
  }
}
